import React, { useState, useMemo } from 'react';
import { X, Download, Code, ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';
import { QueryResult, ColumnDefinition } from './types';

interface ResultViewerProps {
    result: QueryResult;
    onClose: () => void;
}

type SortConfig = {
    column: string;
    direction: 'asc' | 'desc';
} | null;

export const ResultViewer: React.FC<ResultViewerProps> = ({ result, onClose }) => {
    const [showSQL, setShowSQL] = useState(false);
    const [sortConfig, setSortConfig] = useState<SortConfig>(null);

    const sortedRows = useMemo(() => {
        if (!sortConfig) return result.rows;

        const sorted = [...result.rows].sort((a, b) => {
            const aVal = a[sortConfig.column];
            const bVal = b[sortConfig.column];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();

            if (sortConfig.direction === 'asc') {
                return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            } else {
                return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
            }
        });

        return sorted;
    }, [result.rows, sortConfig]);

    const handleSort = (column: ColumnDefinition) => {
        if (!column.sortable) return;

        setSortConfig(current => {
            if (!current || current.column !== column.name) {
                return { column: column.name, direction: 'asc' };
            }
            if (current.direction === 'asc') {
                return { column: column.name, direction: 'desc' };
            }
            return null;
        });
    };

    const formatValue = (value: any, column: ColumnDefinition): string => {
        if (value === null || value === undefined) return '-';

        if (column.dataType === 'number' && typeof value === 'number') {
            if (column.format) {
                const decimals = parseInt(column.format.replace('N', ''));
                return value.toFixed(decimals);
            }
            return value.toLocaleString('nb-NO');
        }

        if (column.dataType === 'date' && value) {
            try {
                const date = new Date(value);
                if (column.format) {
                    return date.toLocaleDateString('nb-NO');
                }
                return date.toLocaleString('nb-NO');
            } catch {
                return String(value);
            }
        }

        return String(value);
    };

    const handleExportCSV = () => {
        const headers = result.columns.map(col => col.displayName).join(';');
        const rows = sortedRows.map(row => 
            result.columns.map(col => {
                const value = row[col.name];
                const formatted = formatValue(value, col);
                // Escape quotes and wrap in quotes if contains semicolon or newline
                return formatted.includes(';') || formatted.includes('\n') 
                    ? `"${formatted.replace(/"/g, '""')}"` 
                    : formatted;
            }).join(';')
        );

        const csv = [headers, ...rows].join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `bizclick-query-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <div className="flex-1 min-w-0 pr-4">
                        <h2 className="text-lg font-semibold text-gray-800 truncate">
                            {result.presentation?.title || result.query}
                        </h2>
                        {result.presentation?.description && (
                            <p className="text-sm text-gray-600 mt-1">
                                {result.presentation.description}
                            </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>{result.rowCount} rader</span>
                            <span>•</span>
                            <span>{result.executionTimeMs}ms</span>
                            {result.tokensUsed && (
                                <>
                                    <span>•</span>
                                    <span>{result.tokensUsed} tokens</span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleExportCSV}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                            <Download size={16} />
                            <span>Eksporter CSV</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* SQL Toggle */}
                {result.generatedSQL && (
                    <div className="px-6 py-2 bg-blue-50 border-b">
                        <button
                            onClick={() => setShowSQL(!showSQL)}
                            className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-medium"
                        >
                            <Code size={16} />
                            <span>{showSQL ? 'Skjul' : 'Vis'} generert SQL</span>
                            {showSQL ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        {showSQL && (
                            <pre className="mt-2 p-3 bg-gray-900 text-green-400 rounded text-xs overflow-x-auto">
                                {result.generatedSQL}
                            </pre>
                        )}
                    </div>
                )}

                {/* Table */}
                <div className="flex-1 overflow-auto p-6">
                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                    {result.columns.map((column, idx) => (
                                        <th
                                            key={idx}
                                            onClick={() => handleSort(column)}
                                            className={`px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b ${
                                                column.sortable ? 'cursor-pointer hover:bg-gray-200' : ''
                                            }`}
                                            style={{ width: column.width || 'auto' }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span>{column.displayName}</span>
                                                {column.sortable && (
                                                    <span className="text-gray-400">
                                                        {sortConfig?.column === column.name ? (
                                                            sortConfig.direction === 'asc' ? (
                                                                <ChevronUp size={16} />
                                                            ) : (
                                                                <ChevronDown size={16} />
                                                            )
                                                        ) : (
                                                            <ArrowUpDown size={14} className="opacity-50" />
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((row, rowIdx) => (
                                    <tr key={rowIdx} className="border-b hover:bg-gray-50">
                                        {result.columns.map((column, colIdx) => (
                                            <td
                                                key={colIdx}
                                                className="px-4 py-3 text-sm text-gray-700"
                                            >
                                                {formatValue(row[column.name], column)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {sortedRows.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            <p>Ingen rader å vise</p>
                        </div>
                    )}
                </div>

                {/* Explanation */}
                {result.explanation && (
                    <div className="px-6 py-4 bg-gray-50 border-t">
                        <p className="text-sm text-gray-700">
                            <span className="font-medium">Forklaring: </span>
                            {result.explanation}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
